import { Controller } from "@hotwired/stimulus"

// Recruiter-Review: Filter-Chips oben, Master-Detail (Card-Liste links,
// Viewer rechts). Klick irgendwo auf eine Card öffnet sie im Viewer.
// Viewer unterstützt PDFs (iframe) und Bilder (img mit Zoom).
// Downloads zeigen DSGVO-Löschhinweis.
const LARGE_FILE_THRESHOLD_BYTES = 20 * 1024 * 1024 // 20 MB
const ACTIVE_ROW_CLASSES = ["border-l-4", "border-blue-600", "shadow-md", "bg-blue-50"]
// Chip-Active: Polygon-Blau mit weißer Schrift. Inactive: neutral-grau.
// Tailwind-Klassen müssen im Controller getoggelt werden (Tailwind-JIT
// kann keine state-abhängigen Klassen zur Compile-Zeit ersetzen).
const CHIP_ACTIVE_CLASSES = ["bg-blue-600", "text-white", "hover:bg-blue-700"]
const CHIP_INACTIVE_CLASSES = ["bg-gray-100", "text-gray-700", "hover:bg-gray-200"]
const ZOOM_MIN = 0.25
const ZOOM_MAX = 5
const ZOOM_STEP = 0.25

export default class extends Controller {
  static targets = [
    "viewer", "viewerFrame", "filename", "empty", "row", "categoryBtn",
    "image", "imageContainer", "imageToolbar", "zoomLabel",
    "prevBtn", "nextBtn"
  ]
  static values = { deletionDate: String }

  connect() {
    this.zoom = 1
    this.activateCategory("")
    this.openFirstPreviewable()
    this.updateNavButtons()
  }

  selectCategory(event) {
    event.preventDefault()
    const catId = String(event.currentTarget.dataset.categoryId || "")
    this.activateCategory(catId)
    this.openFirstPreviewable()
  }

  activateCategory(catId) {
    this.categoryBtnTargets.forEach((btn) => {
      const match = String(btn.dataset.categoryId || "") === catId
      btn.classList.toggle("is-active", match)
      btn.setAttribute("aria-pressed", match ? "true" : "false")
      // Tailwind-Farben explizit setzen — pure Class-Toggle reicht nicht,
      // weil active/inactive unterschiedliche bg-/text-/hover-Klassen brauchen.
      CHIP_ACTIVE_CLASSES.forEach((cls) => btn.classList.toggle(cls, match))
      CHIP_INACTIVE_CLASSES.forEach((cls) => btn.classList.toggle(cls, !match))
    })
    this.rowTargets.forEach((row) => {
      const match = catId === "" || String(row.dataset.categoryId || "") === catId
      row.classList.toggle("hidden", !match)
    })
    this.syncSectionVisibility()
    this.updateNavButtons()
  }

  syncSectionVisibility() {
    this.element.querySelectorAll("[data-docs-section]").forEach((section) => {
      const rows = Array.from(section.querySelectorAll("[data-applicant-docs-target='row']"))
      // Leere Sektionen sichtbar lassen — sie enthalten den Upload-Button.
      // Nur Sektionen mit Rows ausblenden, wenn aktiver Filter alle Rows versteckt.
      if (rows.length === 0) {
        section.classList.remove("hidden")
        return
      }
      const visible = rows.some((r) => !r.classList.contains("hidden"))
      section.classList.toggle("hidden", !visible)
    })
  }

  // Card-Klick: ganze Card ist klickbar. Klicks auf interaktive Kinder
  // (Buttons, Links, Inputs, Selects, Form-Controls) werden NICHT zum
  // Viewer-Open umgewandelt – die machen ihre eigene Aktion.
  cardClick(event) {
    if (event.target.closest("a, button, input, select, textarea, label, [data-no-card-open]")) return
    const row = event.currentTarget
    if (row.dataset.previewable === "true") {
      this.activateRow(row, { userInitiated: true })
    }
  }

  view(event) {
    event.preventDefault()
    event.stopPropagation()
    const row = event.currentTarget.closest("[data-applicant-docs-target='row']")
    if (row) this.activateRow(row, { userInitiated: true })
  }

  forceLoad(event) {
    event.preventDefault()
    const { url, filename, kind } = event.currentTarget.dataset
    if (!url) return
    if (this.hasFilenameTarget) this.filenameTarget.textContent = filename || ""
    if (kind === "image") {
      this.showImage(url)
    } else {
      this.showPdf(url)
    }
  }

  openFirstPreviewable() {
    const first = this.previewableRows()[0]
    if (first) {
      this.activateRow(first, { userInitiated: false })
    } else {
      this.clearViewer()
    }
  }

  // Alle aktuell sichtbaren, previewable Rows in DOM-Reihenfolge.
  previewableRows() {
    return this.rowTargets.filter(
      (r) => !r.classList.contains("hidden") && r.dataset.previewable === "true"
    )
  }

  activeRow() {
    return this.rowTargets.find((r) => r.getAttribute("aria-current") === "true") || null
  }

  prev(event) {
    if (event) event.preventDefault()
    const rows = this.previewableRows()
    if (rows.length === 0) return
    const active = this.activeRow()
    const idx = active ? rows.indexOf(active) : -1
    const target = idx <= 0 ? rows[rows.length - 1] : rows[idx - 1]
    this.activateRow(target, { userInitiated: true })
  }

  next(event) {
    if (event) event.preventDefault()
    const rows = this.previewableRows()
    if (rows.length === 0) return
    const active = this.activeRow()
    const idx = active ? rows.indexOf(active) : -1
    const target = idx === -1 || idx >= rows.length - 1 ? rows[0] : rows[idx + 1]
    this.activateRow(target, { userInitiated: true })
  }

  activateRow(row, { userInitiated = false } = {}) {
    const url = row.dataset.previewUrl
    if (!url) return
    const kind = row.dataset.previewKind || "pdf"
    this.rowTargets.forEach((r) => {
      const isActive = r === row
      ACTIVE_ROW_CLASSES.forEach((cls) => r.classList.toggle(cls, isActive))
      r.setAttribute("aria-current", isActive ? "true" : "false")
    })
    if (this.hasFilenameTarget) this.filenameTarget.textContent = row.dataset.filename || ""

    const size = parseInt(row.dataset.fileSize || "0", 10)
    if (!userInitiated && size > 0 && size > LARGE_FILE_THRESHOLD_BYTES) {
      this.renderLargeFilePrompt(row, size, kind)
      this.updateNavButtons()
      return
    }

    if (kind === "image") {
      this.showImage(url)
    } else {
      this.showPdf(url)
    }
    this.updateNavButtons()
  }

  showPdf(url) {
    this.zoom = 1
    this.applyZoom()
    this.viewerTarget.src = url
    this.viewerTarget.classList.remove("hidden")
    this.hideImage()
    if (this.hasEmptyTarget) this.hideEmpty()
  }

  showImage(url) {
    this.viewerTarget.src = "about:blank"
    this.viewerTarget.classList.add("hidden")
    if (this.hasImageTarget) {
      this.imageTarget.src = url
      this.imageTarget.alt = (this.hasFilenameTarget && this.filenameTarget.textContent) || ""
    }
    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.classList.remove("hidden")
      this.imageContainerTarget.classList.add("flex")
      this.imageContainerTarget.scrollTop = 0
      this.imageContainerTarget.scrollLeft = 0
    }
    if (this.hasImageToolbarTarget) {
      this.imageToolbarTarget.classList.remove("hidden")
      this.imageToolbarTarget.classList.add("flex")
    }
    this.zoom = 1
    this.applyZoom()
    if (this.hasEmptyTarget) this.hideEmpty()
  }

  hideImage() {
    if (this.hasImageContainerTarget) {
      this.imageContainerTarget.classList.add("hidden")
      this.imageContainerTarget.classList.remove("flex")
    }
    if (this.hasImageToolbarTarget) {
      this.imageToolbarTarget.classList.add("hidden")
      this.imageToolbarTarget.classList.remove("flex")
    }
    if (this.hasImageTarget) this.imageTarget.src = ""
  }

  hideEmpty() {
    if (!this.hasEmptyTarget) return
    this.emptyTarget.classList.add("hidden")
  }

  zoomIn(event) {
    if (event) event.preventDefault()
    this.zoom = Math.min(ZOOM_MAX, Math.round((this.zoom + ZOOM_STEP) * 100) / 100)
    this.applyZoom()
  }

  zoomOut(event) {
    if (event) event.preventDefault()
    this.zoom = Math.max(ZOOM_MIN, Math.round((this.zoom - ZOOM_STEP) * 100) / 100)
    this.applyZoom()
  }

  zoomReset(event) {
    if (event) event.preventDefault()
    this.zoom = 1
    this.applyZoom()
  }

  applyZoom() {
    if (!this.hasImageTarget) return
    this.imageTarget.style.transform = `scale(${this.zoom})`
    if (this.hasZoomLabelTarget) {
      this.zoomLabelTarget.textContent = `${Math.round(this.zoom * 100)}%`
    }
  }

  keydown(event) {
    // Pfeil-Navigation nur wenn der Viewer-Frame selbst fokussiert ist,
    // damit Formulareingaben in der Card-Liste nicht versehentlich navigieren.
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.prev()
    } else if (event.key === "+" || event.key === "=") {
      if (this.isImageActive()) {
        event.preventDefault()
        this.zoomIn()
      }
    } else if (event.key === "-") {
      if (this.isImageActive()) {
        event.preventDefault()
        this.zoomOut()
      }
    } else if (event.key === "0") {
      if (this.isImageActive()) {
        event.preventDefault()
        this.zoomReset()
      }
    }
  }

  isImageActive() {
    return this.hasImageContainerTarget && !this.imageContainerTarget.classList.contains("hidden")
  }

  updateNavButtons() {
    const count = this.previewableRows().length
    const disabled = count < 2
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = disabled
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = disabled
  }

  renderLargeFilePrompt(row, size, kind) {
    const mb = (size / 1024 / 1024).toFixed(1)
    this.viewerTarget.src = "about:blank"
    this.viewerTarget.classList.add("hidden")
    this.hideImage()
    if (!this.hasEmptyTarget) return
    this.emptyTarget.classList.remove("hidden")
    this.emptyTarget.classList.remove("text-gray-400", "text-sm")
    const label = kind === "image" ? "Großes Bild" : "Großes PDF"
    this.emptyTarget.innerHTML = `
      <div class="text-center px-4">
        <div class="text-gray-700 mb-1 font-medium">${label} (${mb} MB)</div>
        <div class="text-xs text-gray-500 mb-3">Das Laden kann den Browser verlangsamen.</div>
        <button type="button"
                class="btn btn--default"
                data-action="click->applicant-docs#forceLoad"
                data-url="${row.dataset.previewUrl}"
                data-kind="${kind || "pdf"}"
                data-filename="${(row.dataset.filename || "").replace(/"/g, "&quot;")}">
          Trotzdem laden
        </button>
      </div>`
  }

  clearViewer() {
    this.viewerTarget.src = "about:blank"
    this.viewerTarget.classList.add("hidden")
    this.hideImage()
    if (this.hasEmptyTarget) this.emptyTarget.classList.remove("hidden")
    if (this.hasFilenameTarget) this.filenameTarget.textContent = ""
  }

  confirmDownload(event) {
    event.stopPropagation()
    const date = this.deletionDateValue
    const deletionLine = date
      ? `Das Dokument ist spätestens am ${date} lokal zu löschen (Löschdatum des Bewerbers).`
      : "Das Dokument ist lokal zu löschen, sobald das Löschdatum des Bewerbers erreicht ist."
    const msg = `Datenschutz-Hinweis\n\n${deletionLine}\n\nJetzt herunterladen?`
    if (!confirm(msg)) event.preventDefault()
  }
}
